import React, { useEffect, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import { FormActions, FormError, FormField, FormIntro } from '../../../components/ui/FormModalParts';
import { MISSION_COUNTRIES } from '../utils/missionCountries';

const TYPES = [
  ['daily', 'Daily'],
  ['full', 'Full'],
  ['session', 'Session'],
];

/**
 * Confirms the missions JSON before it is sent.
 * The report markdown and its citations are attached automatically.
 * Day and session numbers stay blank: they are typed in, not guessed.
 */
export default function SendMissionsModal({ open, defaults, sending, missionsEnabled, onClose, onSubmit }) {
  const [form, setForm] = useState(defaults);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(defaults);
      setError('');
    }
  }, [open, defaults]);

  if (!open) return null;

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const needsNumber = form.type === 'daily' || form.type === 'session';

  const submit = (event) => {
    event.preventDefault();
    const eventName = form.eventName.trim();
    const title = form.title.trim();
    const country = form.country.trim();
    const number = Number(form.number);
    if (!eventName || !title || !country) {
      setError('Event name, title and country are required.');
      return;
    }
    if (needsNumber && !(number >= 1)) {
      setError(form.type === 'daily' ? 'Enter the day number.' : 'Enter the session number.');
      return;
    }
    setError('');
    onSubmit({
      eventName,
      title,
      country,
      type: form.type,
      day: form.type === 'daily' ? number : null,
      sessionNumber: form.type === 'session' ? number : null,
    });
  };

  return (
    <Modal isOpen={open} onClose={sending ? () => {} : onClose} title="Send to missions" size="md" className="event-modal">
      <form className="event-modal-form" onSubmit={submit}>
        <FormIntro title="Check the fields, then send">
          Event name and title come from this report. Pick the country and whether this is a daily, full or session report.
          The report text and its citations are sent with them.
        </FormIntro>
        {!missionsEnabled && (
          <FormError message="Turn on missions for this event before sending." />
        )}
        <FormField id="missions-event-name" label="Event name">
          <input id="missions-event-name" className="event-modal-control" value={form.eventName} onChange={set('eventName')} disabled={sending} />
        </FormField>
        <FormField id="missions-title" label="Title">
          <input id="missions-title" className="event-modal-control" value={form.title} onChange={set('title')} disabled={sending} />
        </FormField>
        <FormField id="missions-country" label="Country">
          <select id="missions-country" className="event-modal-control" value={form.country} onChange={set('country')} disabled={sending}>
            <option value="">Select a country</option>
            {MISSION_COUNTRIES.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </FormField>
        <FormField id="missions-type" label="Type">
          <select id="missions-type" className="event-modal-control" value={form.type} onChange={set('type')} disabled={sending}>
            {TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </FormField>
        {needsNumber && (
          <FormField id="missions-number" label={form.type === 'daily' ? 'Day' : 'Session number'}>
            <input
              id="missions-number"
              className="event-modal-control"
              type="number"
              min="1"
              value={form.number}
              onChange={set('number')}
              disabled={sending}
              placeholder={form.type === 'daily' ? 'Day number' : 'Session number'}
            />
          </FormField>
        )}
        <FormError message={error} />
        <FormActions onCancel={onClose} disabled={sending || !missionsEnabled}>
          {sending ? 'Sending...' : 'Send to missions'}
        </FormActions>
      </form>
    </Modal>
  );
}
